import { pdfjsLib } from './pdfjs-config';

export const convertImageToPng = async (file: File): Promise<File> => {
  if (file.type === 'image/png') {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Could not get 2D context from canvas'));
        return;
      }

      ctx.drawImage(img, 0, 0);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            URL.revokeObjectURL(url);
            reject(new Error('Could not convert to PNG'));
            return;
          }

          const fileName = file.name.replace(/\.[^/.]+$/, '') + '.png';
          const newFile = new File([blob], fileName, { type: 'image/png' });

          URL.revokeObjectURL(url);
          resolve(newFile);
        },
        'image/png',
        1
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Error loading image'));
    };

    img.src = url;
  });
};

export const convertPdfPageToPng = async (
  page: any,
  pageNumber: number,
  pdfFileName: string
): Promise<File> => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    try {
      const viewport = page.getViewport({ scale: 2 });
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      if (!context) {
        reject(new Error('Could not get 2D context from canvas'));
        return;
      }
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };
      
      await page.render(renderContext).promise;
      
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Could not convert to PNG'));
            return;
          }
          
          const fileNameBase = pdfFileName.replace(/\.[^/.]+$/, '');
          const fileName = `${fileNameBase}_page${pageNumber}.png`;
          const newFile = new File([blob], fileName, { type: 'image/png' });
          
          resolve(newFile);
        },
        'image/png',
        1
      );
    } catch (error) {
      reject(error);
    }
  });
};

export const convertPdfToPng = async (pdfFile: File): Promise<File[]> => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    try {
      const fileArrayBuffer = await pdfFile.arrayBuffer();
      
      const loadingTask = pdfjsLib.getDocument(fileArrayBuffer);
      const pdf = await loadingTask.promise;
      
      const numPages = pdf.numPages;
      
      const pagePromises: Promise<File>[] = [];
      
      for (let i = 1; i <= numPages; i++) {
        const page = await pdf.getPage(i);
        pagePromises.push(convertPdfPageToPng(page, i, pdfFile.name));
      }
      
      const pngFiles = await Promise.all(pagePromises);
      
      resolve(pngFiles);
    } catch (error) {
      reject(error);
    }
  });
};

export const convertToPng = async (file: File): Promise<File | File[]> => {
  try {
    
    if (file.type === 'application/pdf') {
      try {
        const result = await convertPdfToPng(file);
        return result;
      } catch (pdfError) {
        console.error('PDF conversion failed:', pdfError);
        throw pdfError;
      }
    } else if (file.type.startsWith('image/')) {
      try {
        const result = await convertImageToPng(file);
        return result;
      } catch (imageError) {
        console.error('Image conversion failed:', imageError);
        throw imageError;
      }
    } else {
      console.error(`Unsupported file type: ${file.type}`);
      throw new Error('Unsupported file type');
    }
  } catch (error) {
    console.error('Error converting file:', error);
    throw error;
  }
}; 